import type { paths, components } from "@securityjourney/svc-proctor/dist/gen/proctor";
import type {
  paths as taxonomyPaths,
  components as taxonomyComponents,
} from "@securityjourney/svc-proctor/dist/gen/taxonomy";
import type {
  paths as qaPaths,
  components as qaComponents,
} from "@securityjourney/svc-proctor/dist/gen/qa";

import {
  OpenApiFetchWrapper,
  errorMessage,
  type RpcStatus,
  type ServiceResponse,
} from "./openApiFetchWrapper";
import { getCurrentSelectedTenant } from "../utils/user";

export type ExamInstance = components["schemas"]["securityjourney.proto.proctor.ExamContents"];
export type ExamConfiguration =
  components["schemas"]["securityjourney.proto.proctor.ExamConfiguration"];
export type ExamFilters = components["schemas"]["securityjourney.proto.proctor.Filters"];
export type ExamQuestion = components["schemas"]["securityjourney.proto.proctor.Question"];
export type ExamAnswer = components["schemas"]["securityjourney.proto.proctor.QuestionResponse"];
export type ExamResult = components["schemas"]["securityjourney.proto.proctor.ExamResult"];

export type ExamStatus = "started" | "stopped" | "completed";
export type ExamSections =
  taxonomyComponents["schemas"]["securityjourney.proto.proctor.TaxonomyRoots"]["roots"];

export type UserExam = Omit<
  NonNullable<
    components["schemas"]["securityjourney.proto.proctor.UserInitiatedExamStatusResponse"]["statuses"]
  >[number],
  "status"
> & { status: ExamStatus };

export type UserExamResponse = Omit<
  components["schemas"]["securityjourney.proto.proctor.UserInitiatedExamStatusResponse"],
  "statuses"
> & {
  exams: UserExam[];
};

export type ExamPillarsResponse =
  components["schemas"]["securityjourney.proto.proctor.MyExamPillarsResponse"];
export type ExamPillar =
  components["schemas"]["securityjourney.proto.proctor.MyExamPillarsResponse.Pillar"];
export type PreviousScore = components["schemas"]["securityjourney.proto.proctor.PreviousScore"];
export type TopicScore = components["schemas"]["securityjourney.proto.proctor.TopicScore"];
export type ExamSection =
  taxonomyComponents["schemas"]["securityjourney.proto.proctor.TaxonomyRoots.Section"];
export type QuestionPrompt =
  qaComponents["schemas"]["securityjourney.proto.proctor.Question.Prompt"];

export class ProctorClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    super();
  }

  /**
   * Get the active exam state for a learner
   * Returns 404 if there is no active exam
   */
  async getActiveExam({
    examKey,
    assignmentTaskId,
    topicPrefixes,
    initiatedInTenant,
  }: {
    examKey: string;
    assignmentTaskId?: string;
    topicPrefixes?: string[];
    initiatedInTenant?: string;
  }) {
    return await this.get("/svc/proctor/exam/{exam_key}", {
      params: {
        path: { exam_key: examKey },
        query: {
          assignmentTaskId,
          topicPrefixes,
          ...(initiatedInTenant && { initiatedInTenant }),
        },
      },
    });
  }

  /**
   * Start an exam with the given parameters and settings
   * Returns the questions to be answered
   * If exam is already in progress and compatible, returns existing exam
   */
  async beginExam(
    examKey: string,
    parameters?: ExamConfiguration["parameters"],
    filters?: ExamFilters,
    timeLimit?: string,
    assignmentTaskUuid?: string,
    initiatedInTenant?: string
  ) {
    return await this.post("/svc/proctor/exam/{exam_key}/begin", {
      params: { path: { exam_key: examKey } },
      body: { parameters, filters, timeLimit, assignmentTaskUuid, initiatedInTenant },
    });
  }

  /**
   * Submit an answer to a question on an exam in progress
   * Returns 409 Conflict if time limit has expired
   * Returns 410 Gone if exam has already been submitted
   */
  async answerQuestion(
    examKey: string,
    questionKey: string,
    answer: string,
    examInstanceId?: string,
    initiatedInTenant?: string
  ) {
    return await this.put("/svc/proctor/exam/{exam_key}/answer/{question_key}", {
      params: { path: { exam_key: examKey, question_key: questionKey } },
      body: {
        answer,
        examInstanceId,
        ...(initiatedInTenant && { initiatedInTenant }),
      },
    });
  }

  /**
   * Submit the exam for grading and complete it
   * Can be called even if time limit has expired
   * Repeated calls return the same result
   */
  async submitExam(examKey: string, examInstanceId: string, initiatedInTenant?: string) {
    return await this.post("/svc/proctor/exam/{exam_key}/submit", {
      params: { path: { exam_key: examKey } },
      body: { examInstanceId, ...(initiatedInTenant && { initiatedInTenant }) },
    });
  }

  /**
   * Stop an in-progress exam (admin only)
   * Allows a tenant admin to stop an exam so learner can start over
   */
  async stopExam(examKey: string, directoryUuid: string, examInstanceId: string) {
    return await this.post("/svc/proctor/exam/{exam_key}/learner/{directory_uuid}:stop", {
      params: { path: { exam_key: examKey, directory_uuid: directoryUuid } },
      body: { examInstanceId },
    });
  }

  /**
   * View the results of a specific exam
   * Allows tenant admin/self to view exam results
   */
  async viewExam(examInstanceId: string) {
    return await this.get("/svc/proctor/result/{exam_instance_id}", {
      params: { path: { exam_instance_id: examInstanceId } },
    });
  }

  /**
   * Submit feedback on a question
   * Attachments are optional File objects from an input[type=file]
   */
  async submitFeedback(
    questionKey: string,
    examInstanceId: string,
    feedback: string,
    attachments: File[] = []
  ): Promise<ServiceResponse<void>> {
    const formData = new FormData();
    formData.append("exam_instance_id", examInstanceId);
    formData.append("question_key", questionKey);
    formData.append("feedback", feedback);
    attachments.forEach(attachment => {
      formData.append("file", attachment);
    });
    return await this.postFormData("/svc/proctor/feedback", formData);
  }

  /**
   * Fetch pillar performance data for the learner
   */
  async myExamPillars(options?: {
    excludeAssignments?: boolean;
    includeQuestionResults?: boolean;
  }) {
    const tenant = getCurrentSelectedTenant() ?? "";
    return await this.get("/svc/proctor/{tenant}/my-exam-pillars", {
      params: {
        path: { tenant },
        query: {
          excludeAssignments: options?.excludeAssignments,
          includeQuestionResults: options?.includeQuestionResults,
        },
      },
    });
  }

  /**
   * Fetch the user's exams
   * @param tenant The tenant identifier
   * @param options.includeStatuses Filter by exam statuses
   * @param options.excludeAssignments Whether to exclude assignment exams
   * @returns A promise that resolves to the user exam response
   */
  async fetchUserExams(options?: {
    includeStatuses?: ExamStatus[];
    excludeAssignments?: boolean;
  }): Promise<ServiceResponse<UserExamResponse>> {
    const tenant = getCurrentSelectedTenant() ?? "";
    try {
      const response = await this.client.GET("/svc/proctor/{tenant}/my-exams", {
        params: {
          path: { tenant },
          query: { excludeAssignments: options?.excludeAssignments },
        },
      });
      if (response.data) {
        let exams = (response.data.statuses || []) as UserExam[];
        const includeStatuses = options?.includeStatuses ?? [];
        if (includeStatuses.length > 0) {
          exams = exams.filter(exam => includeStatuses.includes(exam.status));
        }
        const { statuses: _, ...rest } = response.data;
        return { data: { ...rest, exams } as UserExamResponse, error: null };
      }
      return {
        data: null,
        error: new Error(
          (response.error as RpcStatus | undefined)?.message || "Failed to fetch user exams"
        ),
      };
    } catch (error) {
      return { data: null, error: new Error(errorMessage(error) || "Fetch error") };
    }
  }
}

export class TaxonomyClient extends OpenApiFetchWrapper<taxonomyPaths> {
  constructor() {
    super();
  }

  /**
   * List the sections in an exam
   */
  async listSections(examKeys?: string[]): Promise<ServiceResponse<ExamSections>> {
    try {
      const response = await this.client.GET("/svc/proctor/taxonomy", {
        params: { query: { examKeys } },
      });
      if (response.data) {
        return { data: response.data.roots || [], error: null };
      }
      return {
        data: null,
        error: new Error(
          (response.error as RpcStatus | undefined)?.message || "Failed to list sections"
        ),
      };
    } catch (error) {
      return { data: null, error: new Error(errorMessage(error) || "Fetch error") };
    }
  }
}

export class QAClient extends OpenApiFetchWrapper<qaPaths> {
  constructor() {
    super();
  }

  /**
   * Fetch a question by key (for QA/debugging purposes)
   */
  async debugQuestion(key: string) {
    return await this.get("/svc/proctor/question", {
      params: { query: { key } },
    });
  }
}

let proctorClientInstance: ProctorClient | null = null;
let taxonomyClientInstance: TaxonomyClient | null = null;
let qaClientInstance: QAClient | null = null;

export const getTaxonomyClient = (): TaxonomyClient => {
  if (!taxonomyClientInstance) {
    taxonomyClientInstance = new TaxonomyClient();
  }
  return taxonomyClientInstance;
};

export const getProctorClient = (): ProctorClient => {
  if (!proctorClientInstance) {
    proctorClientInstance = new ProctorClient();
  }
  return proctorClientInstance;
};

export const getQAClient = (): QAClient => {
  if (!qaClientInstance) {
    qaClientInstance = new QAClient();
  }
  return qaClientInstance;
};
